import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  getOnboardingStatus, completeOnboarding,
  getServices, createService,
  getBusinessHours, saveBusinessHours,
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';

/**
 * Onboarding guiado — 3 steps inline pra ativar o cliente em minutos.
 *
 * Step 1: Criar primeiro serviço (nome, duração, preço)
 * Step 2: Configurar horários (dias da semana, abertura/fechamento)
 * Step 3: Teste real — link da página pública pra agendar
 *
 * Tudo acontece DENTRO desta tela — sem redirecionar pra outras páginas.
 * O objetivo é que o cliente saia daqui com o sistema funcionando.
 */

const DAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

export default function TenantOnboarding() {
  const { user, reloadUser } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0); // 0, 1, 2
  const [loading, setLoading] = useState(true);
  const [finishing, setFinishing] = useState(false);

  // Step 1 — Serviço
  const [svcForm, setSvcForm] = useState({ name: '', duration_minutes: 60, price: '' });
  const [svcSaving, setSvcSaving] = useState(false);
  const [svcDone, setSvcDone] = useState(false);

  // Step 2 — Horários
  const [hours, setHours] = useState(DAYS.map((_, i) => ({
    day_of_week: i,
    is_open: i >= 1 && i <= 5, // Seg-Sex aberto por default
    open_time: '08:00',
    close_time: '18:00',
    slot_duration_minutes: 30,
  })));
  const [hoursSaving, setHoursSaving] = useState(false);
  const [hoursDone, setHoursDone] = useState(false);

  // Step 3 — Teste
  const slug = user?.tenant?.slug;

  // Verifica estado atual pra não refazer steps já concluídos
  useEffect(() => {
    (async () => {
      try {
        const [svcR, hoursR] = await Promise.all([
          getServices().catch(() => ({ data: { services: [] } })),
          getBusinessHours().catch(() => ({ data: { hours: [] } })),
        ]);

        const hasServices = (svcR.data.services || []).length > 0;
        const hasHours = (hoursR.data.hours || hoursR.data || []).some(h => h.is_open);

        if (hasServices) setSvcDone(true);
        if (hasHours) {
          setHoursDone(true);
          // Carrega horários existentes
          const existing = hoursR.data.hours || hoursR.data || [];
          if (existing.length > 0) {
            setHours(DAYS.map((_, i) => {
              const h = existing.find(x => x.day_of_week === i) || {};
              return {
                day_of_week: i,
                is_open: !!h.is_open,
                open_time: h.open_time || '08:00',
                close_time: h.close_time || '18:00',
                slot_duration_minutes: h.slot_duration_minutes || 30,
              };
            }));
          }
        }

        // Pula pra o step correto
        if (hasServices && hasHours) setStep(2);
        else if (hasServices) setStep(1);
      } catch {}
      setLoading(false);
    })();
  }, []);

  // ── Step 1: Criar serviço ──────────────────────────────────────────
  const handleCreateService = async e => {
    e.preventDefault();
    if (!svcForm.name.trim()) return toast.error('Informe o nome do serviço');
    setSvcSaving(true);
    try {
      await createService({
        name: svcForm.name.trim(),
        duration_minutes: parseInt(svcForm.duration_minutes) || 60,
        price: parseFloat(svcForm.price) || 0,
        active: true,
      });
      toast.success('Serviço criado!');
      setSvcDone(true);
      setStep(1);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao criar serviço');
    } finally { setSvcSaving(false); }
  };

  // ── Step 2: Salvar horários ────────────────────────────────────────
  const toggleDay = i => setHours(h => h.map((d, idx) => idx === i ? { ...d, is_open: !d.is_open } : d));
  const setTime = (i, field, val) => setHours(h => h.map((d, idx) => idx === i ? { ...d, [field]: val } : d));

  const handleSaveHours = async () => {
    setHoursSaving(true);
    try {
      await saveBusinessHours(hours);
      toast.success('Horários configurados!');
      setHoursDone(true);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar horários');
    } finally { setHoursSaving(false); }
  };

  // ── Finalizar ──────────────────────────────────────────────────────
  const handleFinish = async () => {
    setFinishing(true);
    try {
      await completeOnboarding();
      await reloadUser();
      toast.success('Pronto! Seu sistema está configurado.');
      navigate('/app');
    } catch { navigate('/app'); }
    finally { setFinishing(false); }
  };

  if (loading) return <div className="page-body" style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}><div className="spinner" /></div>;

  const progress = (svcDone ? 1 : 0) + (hoursDone ? 1 : 0) + (step === 2 ? 1 : 0);

  return (
    <div className="page-body" style={{ maxWidth: 580, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ fontSize: 44, marginBottom: 8 }}>
          {step === 0 ? '🛎️' : step === 1 ? '🕐' : '🎉'}
        </div>
        <h2 style={{ fontSize: 22, margin: '0 0 6px' }}>
          {step === 0 ? 'Crie seu primeiro serviço' :
           step === 1 ? 'Configure seus horários' :
           'Tudo pronto!'}
        </h2>
        <p style={{ color: 'var(--gray-500)', fontSize: 14, margin: 0 }}>
          Olá, <strong>{user?.name?.split(' ')[0]}</strong>!
          {step === 0 && ' Vamos configurar seu negócio em 3 passos rápidos.'}
          {step === 1 && ' Quase lá — defina quando você atende.'}
          {step === 2 && ' Seu sistema está funcionando. Teste agora!'}
        </p>
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--gray-400)', marginBottom: 6 }}>
          <span>Passo {Math.min(step + 1, 3)} de 3</span>
          <span>{Math.round((progress / 3) * 100)}%</span>
        </div>
        <div style={{ height: 6, background: 'var(--gray-200)', borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ height: '100%', background: progress === 3 ? '#10b981' : 'var(--primary)', width: `${(progress / 3) * 100}%`, borderRadius: 6, transition: 'width .4s' }} />
        </div>
      </div>

      {/* ── STEP 0: Criar serviço ──────────────────────────────────── */}
      {step === 0 && (
        <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--gray-200)', padding: 24 }}>
          <p style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 0, marginBottom: 20 }}>
            Qual serviço principal você oferece? Exemplo: "Corte masculino", "Consulta inicial", "Sessão de fisioterapia".
          </p>
          <form onSubmit={handleCreateService}>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label style={{ fontWeight: 600, fontSize: 13 }}>Nome do serviço *</label>
              <input className="input" value={svcForm.name} onChange={e => setSvcForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ex: Corte + Barba" autoFocus />
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label style={{ fontWeight: 600, fontSize: 13 }}>Duração (min)</label>
                <select className="input" value={svcForm.duration_minutes} onChange={e => setSvcForm(f => ({ ...f, duration_minutes: e.target.value }))}>
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>1 hora</option>
                  <option value={90}>1h30</option>
                  <option value={120}>2 horas</option>
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label style={{ fontWeight: 600, fontSize: 13 }}>Preço (R$)</label>
                <input className="input" type="number" step="0.01" min="0" value={svcForm.price}
                  onChange={e => setSvcForm(f => ({ ...f, price: e.target.value }))} placeholder="50.00" />
              </div>
            </div>
            <button type="submit" className={`btn btn-primary btn-block${svcSaving ? ' btn-loading' : ''}`} disabled={svcSaving}>
              {svcSaving ? '' : 'Criar serviço e continuar'}
            </button>
          </form>
        </div>
      )}

      {/* ── STEP 1: Horários ────────────────────────────────────────── */}
      {step === 1 && (
        <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--gray-200)', padding: 24 }}>
          <p style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 0, marginBottom: 16 }}>
            Marque os dias que você atende e ajuste os horários. Você pode mudar depois em Configurações.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
            {hours.map((h, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                background: h.is_open ? 'var(--gray-50)' : 'transparent',
                borderRadius: 8, border: '1px solid var(--gray-200)',
                opacity: h.is_open ? 1 : 0.5,
              }}>
                <input type="checkbox" checked={h.is_open} onChange={() => toggleDay(i)}
                  style={{ width: 18, height: 18, cursor: 'pointer' }} />
                <span style={{ width: 70, fontWeight: 600, fontSize: 13 }}>{DAYS[i]}</span>
                {h.is_open && (
                  <>
                    <input type="time" className="input" value={h.open_time} onChange={e => setTime(i, 'open_time', e.target.value)}
                      style={{ width: 100, padding: '4px 8px', fontSize: 13 }} />
                    <span style={{ color: 'var(--gray-400)', fontSize: 12 }}>até</span>
                    <input type="time" className="input" value={h.close_time} onChange={e => setTime(i, 'close_time', e.target.value)}
                      style={{ width: 100, padding: '4px 8px', fontSize: 13 }} />
                  </>
                )}
                {!h.is_open && <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>Fechado</span>}
              </div>
            ))}
          </div>
          <button className={`btn btn-primary btn-block${hoursSaving ? ' btn-loading' : ''}`}
            disabled={hoursSaving || !hours.some(h => h.is_open)} onClick={handleSaveHours}>
            {hoursSaving ? '' : 'Salvar horários e continuar'}
          </button>
        </div>
      )}

      {/* ── STEP 2: Teste ───────────────────────────────────────────── */}
      {step === 2 && (
        <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--gray-200)', padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
          <h3 style={{ margin: '0 0 8px' }}>Seu sistema está no ar!</h3>
          <p style={{ color: 'var(--gray-500)', fontSize: 14, marginBottom: 20 }}>
            Seus clientes já podem agendar pela sua página:
          </p>

          {slug && (
            <div style={{
              background: 'var(--gray-50)', borderRadius: 10, padding: '14px 16px',
              marginBottom: 20, border: '1px solid var(--gray-200)',
            }}>
              <div style={{ fontSize: 11, color: 'var(--gray-400)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>
                Sua página de agendamento
              </div>
              <a href={`/agendar/${slug}`} target="_blank" rel="noopener noreferrer"
                style={{ color: 'var(--primary)', fontWeight: 700, fontSize: 15, wordBreak: 'break-all' }}>
                atendoapp.com/agendar/{slug}
              </a>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
            {slug && (
              <a href={`/agendar/${slug}`} target="_blank" rel="noopener noreferrer"
                className="btn btn-secondary btn-block">
                Testar agendamento
              </a>
            )}
            <button className={`btn btn-primary btn-block${finishing ? ' btn-loading' : ''}`}
              onClick={handleFinish} disabled={finishing}>
              {finishing ? '' : 'Ir para o painel'}
            </button>
          </div>
        </div>
      )}

      {/* Skip */}
      {step < 2 && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={handleFinish} disabled={finishing}
            style={{ fontSize: 13, color: 'var(--gray-400)' }}>
            Pular e configurar depois
          </button>
        </div>
      )}
    </div>
  );
}
